import styled, { x, css } from '@xstyled/styled-components';
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import ReactDOM from 'react-dom';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { Pause, Play } from '~/ui/buttons';
import CheckIcon from '~/assets/icons/check.svg';
import {
  CardAudioProgress,
  CardPlayButton,
  CARD_COLORS,
  formatAudioTime
} from '../../audio-player/HorizontalAudioPlayer';
import { Card } from '~/ui/card/Card';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { SpeakerIcon } from './SpeakerIcon';
import { TickMarkIcon } from './TickMarkIcon';
import { usePopup } from './usePopup';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useCanControl } from '~/ui/video-player/useCanControl';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { drillAnimation } from '~/ui/animation';
import { isMobile } from 'react-device-detect';
import { TrainingState, useTrainingStore } from '~/training/state';
import { t } from 'ttag';
import closeIcon from '~/assets/icons/close.svg';
import { EditorMediaFile, useEditorStore } from '~/shell/Store';
import NarrationPopup from '~/editor/narration/NarrationPopup';
import { ImageMapPopupLayoutContext } from '../../image-map/ImageMapPopupLayoutContext';
import { getImageMapBoundsEl } from '../../image-map/imageMapBounds';

const selector = (s: MediaPlayerStore) =>
  select(
    s,
    'loadMedia',
    'status',
    'toggle',
    'play',
    'loadSubtitles',
    'pause',
    'currentTime'
  );

const playerSelector = (s: MediaPlayerStore) => s.player();

const seqSelector = (s: SequenceStore) =>
  select(s, 'setAudioPlayed', 'addBlocker', 'removeBlocker');

interface DrillBoxProps {
  $btnSize?: number;
  $drillBg?: string;
  $drillOpacity?: number;
}

const DrillBoxAnimation = styled.div<DrillBoxProps>`
  width: ${p => `${p.$btnSize ?? 85}px`};
  height: ${p => `${p.$btnSize ?? 85}px`};
  animation: ${drillAnimation} 2.3s ease infinite;
  position: absolute;
  background-color: ${p => p.$drillBg ?? p.theme.colors.accentDark};
  border-radius: 50%;
  opacity: ${p => p.$drillOpacity ?? 0.5};
  z-index: 1;
`;

const NumberIcon = styled.p`
  font-size: ${p => (p.children === '+' ? 40 : 24)}px;
  font-weight: 700;
  display: block;
  margin-top: ${p => (p.children === '+' ? 1 : -3)}px;
  line-height: 20px;
`;

const CloseIcon = styled(closeIcon)`
  transform: rotate(180deg);
  color: ${p => p.theme.colors.black};
  width: 14px;
  height: 14px;
`;

const CloseButton = styled.button`
  position: absolute;
  z-index: 1;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 2px solid #000;
  border-radius: 4px; /* square with subtle rounding */
  padding: 0;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const selectorVisualFeedback = (s: TrainingState) =>
  select(s, 'visualFeedback');

const PopupContent = styled.div<{
  isScrollEnabled?: boolean;
  gridArea?: string;
}>`
  ${p =>
    p.isScrollEnabled &&
    css`
      max-height: 200px;
      overflow-y: scroll;
    `}
`;

export const PopupButton = ({
  mediaUrl,
  id,
  name,
  body,
  blocking,
  subtitlesUrl,
  title,
  replay,
  isPopupCentered,
  openPopupUnderButton = false,
  popupFixedOffsetLeftPx: itemOffsetLeft,
  popupFixedOffsetTopPx: itemOffsetTop,
  shouldShow = true,
  btnSize = 64,
  scroll = false,
  parentId,
  editable = true,
  generated = false
}: {
  mediaUrl?: string;
  id: string;
  name: string;
  body: string;
  shouldShow: boolean;
  blocking: boolean;
  subtitlesUrl?: string;
  title?: number | string;
  replay?: boolean;
  isPopupCentered?: boolean;
  openPopupUnderButton?: boolean;
  popupFixedOffsetLeftPx?: number;
  popupFixedOffsetTopPx?: number;
  btnSize?: number;
  scroll?: boolean;
  parentId?: string;
  editable?: boolean;
  generated?: boolean;
}) => {
  const { contentEditable, updateFile } = useEditorStore();
  const [showAudioPopup, setShowAudioPopup] = useState(false);

  const { open, setOpen, popupRef } = usePopup();
  const { loadMedia, status, toggle, play, loadSubtitles, pause, currentTime } =
    useMediaPlayerStore(selector, shallow);
  const player = useMediaPlayerStore(playerSelector);
  const [duration, setDuration] = useState(0);
  const ref = useRef<HTMLDivElement>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [top, setTop] = useState<number>();
  const [left, setLeft] = useState<number>();
  const [isClicked, setIsClicked] = useState<boolean>(false);
  const { addBlocker, removeBlocker, setAudioPlayed } = useSequenceStore(
    seqSelector,
    shallow
  );

  const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
  const imageMapPopupLayout = useContext(ImageMapPopupLayoutContext);
  const offsetLeft =
    itemOffsetLeft ?? imageMapPopupLayout?.popupFixedOffsetLeftPx ?? 0;
  const offsetTop =
    itemOffsetTop ?? imageMapPopupLayout?.popupFixedOffsetTopPx ?? 0;

  useGlobalMedia();

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;

  useEffect(() => {
    if (globalPlayer && globalPlayer.pause) {
      try {
        globalPlayer.pause();
      } catch (e) {
        // Ignore errors when pausing uninitialized player
      }
    }
  }, []);

  useEffect(() => {
    if (!mediaUrl) {
      addBlocker(id);
    }

    return () => {
      removeBlocker(id);
    };
  }, []);

  useEffect(() => {
    if (!blocking) return;

    if (mediaUrl) addBlocker(id);

    if (
      (mediaUrl && (status === 'nomedia' || status === 'done')) ||
      (!mediaUrl && open)
    ) {
      removeBlocker(id);
    }
  }, [status, open, mediaUrl]);

  useLayoutEffect(() => {
    if (!open) return;

    let attempts = 0;
    const maxAttempts = 15;

    function updatePosition() {
      const rect = ref?.current?.getBoundingClientRect();
      const popupEl = popupRef?.current;
      const popupRect = popupEl?.getBoundingClientRect();
      if (!rect || !popupEl) return;

      const popupReady =
        popupRect && popupRect.width >= 8 && popupRect.height >= 8;

      if (!popupReady && attempts++ < maxAttempts) {
        requestAnimationFrame(updatePosition);
        return;
      }

      if (!popupRect || popupRect.width < 4) return;

      const anchoredBelowHotspot = () => {
        let nextLeft = rect.left + rect.width / 2 - popupRect.width / 2;
        const shouldClamp =
          openPopupUnderButton || imageMapPopupLayout?.clampPopupToImageMap;
        if (shouldClamp) {
          const mapEl = getImageMapBoundsEl(ref.current ?? undefined);
          const mapRect = mapEl?.getBoundingClientRect();
          if (mapRect) {
            const pad = 8;
            nextLeft = Math.max(
              mapRect.left + pad,
              Math.min(mapRect.right - popupRect.width - pad, nextLeft)
            );
          }
        }
        setTop(rect.bottom);
        setLeft(nextLeft);
      };

      if (isPopupCentered) {
        setTop(window.innerHeight / 2 - popupRect.height / 2);
        setLeft((window.innerWidth - 350) / 2);
        return;
      }

      if (openPopupUnderButton) {
        anchoredBelowHotspot();
        return;
      }

      anchoredBelowHotspot();
    }

    window.addEventListener('resize', updatePosition);
    updatePosition();

    return () => window.removeEventListener('resize', updatePosition);
  }, [open, openPopupUnderButton, isPopupCentered, imageMapPopupLayout]);

  useEffect(() => {
    if (!mediaUrl) return;
    loadMedia(mediaUrl, subtitlesUrl, 'audio', false, 0);
    loadSubtitles(subtitlesUrl);
  }, []);

  useEffect(() => {
    if (!player) {
      setDuration(0);
      return;
    }

    const updateDuration = () => {
      const nextDuration = player.duration();
      if (Number.isFinite(nextDuration) && nextDuration > 0) {
        setDuration(nextDuration);
      }
    };

    player.on('loadedmetadata', updateDuration);
    player.on('durationchange', updateDuration);
    updateDuration();

    return () => {
      player.off('loadedmetadata', updateDuration);
      player.off('durationchange', updateDuration);
    };
  }, [player, mediaUrl]);

  useEffect(() => {
    if (status != 'done' && !open) {
      pause();
    }
  }, [status, open]);

  useEffect(() => {
    if (status === 'done') {
      setOpen(false);
      setAudioPlayed(id);
    }
  }, [status]);

  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      buttonRef.current?.focus();
    }
    prevOpenRef.current = open;
  }, [open]);

  // Hide the button when another media player is playing
  const canControl = useCanControl() || status != 'playing';

  const clickHandler = () => {
    if (contentEditable && editable) {
      setShowAudioPopup(true);
      return;
    }

    if (globalPlayer) {
      try {
        globalPlayer.pause();
      } catch (e) {
        // Ignore errors when pausing uninitialized player
      }
    }

    if (!mediaUrl) {
      setIsClicked(true);
      removeBlocker(id);
    }

    if (status === 'done' && !replay) return;

    play();
    setOpen(true);
  };

  const [isScrollEnabled, setScrollEnabled] = useState(false);

  if (scroll) {
    useEffect(() => {
      const handleResize = () => {
        if (popupRef?.current?.clientHeight! + 36 >= window.innerHeight) {
          setScrollEnabled(true);
        }
      };

      isMobile ? setScrollEnabled(true) : setTimeout(() => handleResize(), 0);

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [open]);
  }

  const getContentForPopup = (name: string, body: string) => {
    const isDefaultAudioPointTitle =
      !mediaUrl && name?.trim() === 'Audio Point';

    const displayName = isDefaultAudioPointTitle ? '' : name;

    if (displayName && body == '' && shouldShow) {
      return (
        <x.span
          overflow="hidden"
          textOverflow="ellipsis"
          gridArea="title"
          dangerouslySetInnerHTML={{ __html: generated ? '' : displayName }}
        />
      );
    } else {
      return (
        <PopupContent
          isScrollEnabled={isScrollEnabled || false}
          tabIndex={isScrollEnabled ? 0 : -1}
          gridArea="title"
          style={
            !mediaUrl && (!displayName || displayName.trim() === '')
              ? { paddingTop: '16px' }
              : undefined
          }
        >
          {shouldShow && (
            <>
              {displayName && (
                <x.h2
                  mb="15px"
                  dangerouslySetInnerHTML={{ __html: displayName }}
                />
              )}
              <div
                dangerouslySetInnerHTML={{ __html: generated ? '' : body }}
              />
            </>
          )}
        </PopupContent>
      );
    }
  };

  const getStartIcon = () => {
    if (
      typeof title === 'number' ||
      (typeof title === 'string' && title !== '')
    ) {
      return <NumberIcon className="pressable">{title}</NumberIcon>;
    } else {
      return <SpeakerIcon className="pressable" aria-hidden />;
    }
  };

  const isListened =
    (mediaUrl && status === 'done' && !replay) || (!mediaUrl && isClicked);
  const ariaLabel = isListened ? t`Audio already played` : t`Play the audio`;

  const getBtnIcon = () => {
    if (title) {
      return <NumberIcon className="pressable">{title}</NumberIcon>;
    }

    if (isClicked && !mediaUrl) {
      return <TickMarkIcon className="pressable" />;
    }

    // For text-only popups, original UI showed a '+' button (no number)
    return (
      <x.span fontSize="40px" className="pressable">
        +
      </x.span>
    );
  };

  const escFunction = (event: React.KeyboardEvent) => {
    if (event.keyCode === 27) {
      pause();
      setOpen(false);
    }
  };

  const onSelectNarration = (audio: EditorMediaFile) => {
    if (id && audio?.id) {
      updateFile(id.toString(), audio, 'audios', parentId);
    }

    setShowAudioPopup(false);
  };

  return (
    <x.div
      w={`${btnSize}px`}
      h={`${btnSize}px`}
      backgroundColor={
        canControl && status === 'done'
          ? 'rgba(255,255,255,0.2)'
          : 'transparent'
      }
      borderRadius="50%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      position="relative"
      zIndex={4}
      pointerEvents="auto"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      className="pressable audioPopUpBtn"
    >
      {canControl && (status !== 'done' || replay) && visualFeedback && (
        <DrillBoxAnimation
          className="drillBoxEffect"
          $btnSize={btnSize - 5}
          $drillOpacity={1}
        />
      )}
      {open &&
        status == 'playing' &&
        ReactDOM.createPortal(
          <>
            <MediaCaptions />
          </>,
          document.getElementById('root')!
        )}
      {canControl && (
        <x.button
          ref={buttonRef}
          onClick={clickHandler}
          backgroundColor="white"
          color="black"
          borderRadius="50%"
          w={`${btnSize - 10}px`}
          h={`${btnSize - 10}px`}
          display="flex"
          justifyContent="center"
          alignItems="center"
          overflow="hidden"
          zIndex={2}
          aria-label={ariaLabel}
          className="pressable"
        >
          {mediaUrl ? (
            status === 'done' ? (
              <TickMarkIcon className="pressable" aria-hidden />
            ) : (
              getStartIcon()
            )
          ) : (
            getBtnIcon()
          )}
        </x.button>
      )}
      <AudioPlayer />
      {open &&
        ReactDOM.createPortal(
          <Card
            ref={popupRef}
            position="fixed"
            w="350px"
            p="md"
            onClick={e => e.stopPropagation()}
            top={`${(top ?? 0) + offsetTop}px`}
            left={`${(left ?? 0) + offsetLeft}px`}
            display="grid"
            gridTemplateColumns="auto 1fr"
            gridTemplateRows="auto auto"
            gridTemplateAreas="'button title' 'button progress'"
            paddingTop={mediaUrl ? '16px' : '40px'}
            gap={mediaUrl ? 'md' : 'md'}
            onKeyDown={escFunction}
          >
            {!mediaUrl && (
              <CloseButton
                onClick={() => setOpen(false)}
                aria-label={t`close`}
                role="button"
                autoFocus
              >
                <CloseIcon />
              </CloseButton>
            )}
            {getContentForPopup(name, body)}
            {mediaUrl && (
              <x.div
                style={{ gridColumn: '1 / -1' }}
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="8px"
                w="100%"
              >
                <CardPlayButton
                  type="button"
                  autoFocus
                  onClick={toggle}
                  aria-label={
                    status === 'playing'
                      ? 'Pause'
                      : status === 'done'
                        ? 'Replay'
                        : 'Play'
                  }
                >
                  {status === 'playing' ? (
                    <Pause />
                  ) : status === 'done' ? (
                    <CheckIcon />
                  ) : (
                    <Play id="playBtnSvg" />
                  )}
                </CardPlayButton>
                <x.div
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                  gap="4px"
                  flex="1"
                  minWidth="0"
                >
                  <x.div
                    fontWeight={600}
                    fontSize="14px"
                    lineHeight="20px"
                    style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      color: CARD_COLORS.text
                    }}
                  >
                    {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
                  </x.div>
                  <CardAudioProgress w="100%" />
                </x.div>
              </x.div>
            )}
          </Card>,
          document.getElementById('root')!
        )}
      {showAudioPopup &&
        contentEditable &&
        ReactDOM.createPortal(
          <NarrationPopup
            onClose={() => setShowAudioPopup(false)}
            skipUpdate={true}
            onSelectCallback={onSelectNarration}
            id={id}
            skipSequenceNarration={true}
          />,
          document.body
        )}
    </x.div>
  );
};
